import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Card, Btn, Empty } from "../components/UI";
import { FLabel, FInput, FTextarea } from "./engagement/FormControls";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { Sparkles, Plus, Pencil, Trash2, ShieldCheck, ChevronRight, ChevronLeft, Check, Loader2, UserCog, Grid3X3 } from "lucide-react";
import { toast } from "sonner";

const BUILTIN = [
  { key: "admin", name: "Administrator", desc: "Full access to every module including settings, users, billing and audit." },
  { key: "clinician", name: "Clinician", desc: "Clinical documentation, forms, trackers, groups, scheduling and reports." },
  { key: "front_desk", name: "Front Desk", desc: "Client demographics, scheduling, tasks, and letters. No clinical signing." },
  { key: "billing", name: "Billing", desc: "Revenue cycle, insurance, authorizations, claims and financial reports." },
];
const ACTION_LABELS = { view: "View", create: "Create", edit: "Edit", delete: "Delete", sign: "Sign", export: "Export/Print" };

function StepDots({ step }) {
  const steps = ["Describe", "Permissions", "Review & Save"];
  return (
    <div className="mb-5 flex items-center gap-2" data-testid="role-wizard-steps">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-[#2E7D32] text-white" : i === step ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#84675B]"}`}>
            {i < step ? <Check size={13} /> : i + 1}
          </div>
          <span className={`text-xs font-semibold ${i === step ? "text-[#3E2723]" : "text-[#84675B]"}`}>{s}</span>
          {i < steps.length - 1 && <ChevronRight size={13} className="text-[#D7CCC8]" />}
        </div>
      ))}
    </div>
  );
}

function RoleWizard({ modules, actions, editRole, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState(editRole?.name || "");
  const [description, setDescription] = useState(editRole?.description || "");
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [summary, setSummary] = useState(editRole?.summary || "");
  const [rationale, setRationale] = useState({});
  const [perms, setPerms] = useState(editRole?.permissions || {});
  const [busy, setBusy] = useState(false);

  const askAI = async () => {
    if (aiText.trim().length < 10) return toast.error("Describe the role and duties in a sentence or two");
    setAiBusy(true);
    try {
      const r = await api.post("/settings/roles/ai-suggest", { description: aiText });
      if (!name) setName(r.data.name || "");
      if (!description) setDescription(aiText);
      setSummary(r.data.summary || "");
      setPerms(r.data.permissions || {});
      setRationale(r.data.rationale || {});
      toast.success("The Permission Architect drafted the profile. Review each step and adjust as needed");
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  const toggle = (mod, act) => setPerms((p) => {
    const cur = p[mod] || [];
    return { ...p, [mod]: cur.includes(act) ? cur.filter((a) => a !== act) : [...cur, act] };
  });

  const save = async () => {
    if (!name.trim()) return toast.error("Role name is required");
    setBusy(true);
    try {
      const body = { name, description, summary, permissions: perms };
      if (editRole) await api.put(`/settings/roles/${editRole.id}`, body);
      else await api.post("/settings/roles", body);
      toast.success(`Role "${name}" ${editRole ? "updated" : "created"}, permissions enforced immediately`);
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const grantedCount = Object.values(perms).flat().length;

  return (
    <SlidePanel open onClose={onClose} title={editRole ? `Edit Role: ${editRole.name}` : "Create Role"}
      subtitle="Step-by-step permission profile, HubSpot-style" width="max-w-2xl" testId="role-wizard">
      <StepDots step={step} />

      {step === 0 && (
        <div className="space-y-4 pb-8">
          <div className="rounded-2xl border border-[#D8C9F5] bg-gradient-to-br from-[#F7F2FF] to-[#FDFCF8] p-4" data-testid="role-ai-box">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#5E35B1]"><Sparkles size={15} /> AI Permission Architect <span className="ml-1 rounded-full bg-[#EDE7F6] px-1.5 py-0.5 text-[9px] font-bold text-[#4527A0]">Draft only</span></div>
            <p className="mb-2 text-xs text-[#6D4C41]">Describe the role and its duties in plain English. The Permission Architect will configure least-privilege permissions across all 15 modules, and it will write a summary you can approve or modify.</p>
            <FTextarea data-testid="role-ai-input" rows={3} value={aiText} onChange={(e) => setAiText(e.target.value)}
              placeholder={'e.g., "Intake coordinator: answers referral calls, registers new clients, verifies insurance, and books intake appointments. Should never see clinical notes or sign anything."'} />
            <div className="mt-2 flex justify-end">
              <Btn onClick={askAI} disabled={aiBusy} data-testid="role-ai-generate-btn" className="bg-[#5E35B1] hover:bg-[#4527A0]">
                {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {aiBusy ? "Thinking…" : "Generate with AI"}
              </Btn>
            </div>
            {summary && (
              <div className="mt-3 rounded-xl bg-white/70 border border-[#E5DCF7] p-3" data-testid="role-ai-summary">
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#5E35B1]">AI Summary</div>
                <p className="mt-1 text-sm text-[#3E2723]">{summary}</p>
                <p className="mt-1 text-[11px] text-[#7B6157]">{grantedCount} permissions pre-selected across {Object.keys(perms).length} modules, review in the next step.</p>
              </div>
            )}
          </div>
          <div><FLabel>Role name *</FLabel><FInput data-testid="role-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Intake Coordinator" disabled={!!editRole} /></div>
          <div><FLabel>Description</FLabel><FTextarea data-testid="role-desc-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div className="flex justify-end"><Btn onClick={() => setStep(1)} data-testid="role-wizard-next-1" disabled={!name.trim()}>Next: Permissions <ChevronRight size={14} /></Btn></div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-2 pb-8" data-testid="role-wizard-permissions">
          <p className="mb-2 text-xs text-[#7B6157]">Toggle exactly what this role can do in each module. {summary ? "Suggested selections are pre-filled, adjust freely." : ""}</p>
          {modules.map((m) => {
            const granted = perms[m.key] || [];
            return (
              <div key={m.key} className={`rounded-xl border p-3 ${granted.length ? "border-[#C8E6C9] bg-[#FBFEF9]" : "border-[#EFEBE0]"}`} data-testid={`role-perm-module-${m.key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#3E2723]">{m.label}</span>
                  <span className="text-[10px] text-[#84675B]">{granted.length ? `${granted.length} granted` : "No access"}</span>
                </div>
                {rationale[m.key] && <p className="mt-0.5 text-[11px] italic text-[#5E35B1]">AI: {rationale[m.key]}</p>}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {actions.map((a) => (
                    <button key={a} data-testid={`role-perm-${m.key}-${a}`} onClick={() => toggle(m.key, a)}
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${granted.includes(a) ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#7B6157] hover:bg-[#EDE7DC]"}`}>
                      {ACTION_LABELS[a]}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="flex justify-between pt-2">
            <Btn variant="secondary" onClick={() => setStep(0)}><ChevronLeft size={14} /> Back</Btn>
            <Btn onClick={() => setStep(2)} data-testid="role-wizard-next-2">Next: Review <ChevronRight size={14} /></Btn>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4 pb-8" data-testid="role-wizard-review">
          <div className="rounded-xl border border-[#EFEBE0] p-4">
            <div className="font-heading text-base font-bold text-[#3E2723]">{name}</div>
            {description && <p className="mt-1 text-sm text-[#6D4C41]">{description}</p>}
            {summary && <p className="mt-2 rounded-lg bg-[#F7F2FF] p-2 text-xs text-[#5E35B1]">{summary}</p>}
          </div>
          <div className="rounded-xl border border-[#EFEBE0] overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F5F2EB] text-left text-[10px] uppercase tracking-wide text-[#7B6157]"><th className="px-3 py-2">Module</th><th className="px-3 py-2">Access</th></tr></thead>
              <tbody>
                {modules.map((m) => {
                  const g = perms[m.key] || [];
                  return (
                    <tr key={m.key} className="border-t border-[#F5F2EB]">
                      <td className="px-3 py-1.5 font-medium text-[#3E2723]">{m.label}</td>
                      <td className="px-3 py-1.5 text-xs">{g.length ? <span className="text-[#2E7D32] font-semibold">{g.map((a) => ACTION_LABELS[a]).join(", ")}</span> : <span className="text-[#84675B]">No access</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between">
            <Btn variant="secondary" onClick={() => setStep(1)}><ChevronLeft size={14} /> Modify</Btn>
            <Btn onClick={save} disabled={busy} data-testid="role-wizard-save-btn"><Check size={14} /> {busy ? "Saving…" : "Approve & Save Role"}</Btn>
          </div>
        </div>
      )}
    </SlidePanel>
  );
}

export default function RolesPermissions({ MatrixConsole }) {
  const [tab, setTab] = useState("roles");
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState(null);
  const [wizard, setWizard] = useState(null); // false | {edit} | "new"
  const [delRole, setDelRole] = useState(null);
  const load = () => Promise.all([
    api.get("/settings/roles").then((r) => setRoles(r.data)),
    api.get("/settings/permissions").then((r) => setMeta(r.data)),
  ]).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const remove = async () => {
    try { await api.delete(`/settings/roles/${delRole.id}`); toast.success("Role deleted"); setDelRole(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const builtinAccess = (key) => {
    if (!meta) return "";
    const mods = meta.modules.filter((m) => (meta.matrix[m.key]?.view || []).includes(key)).length;
    return `${mods}/${meta.modules.length} modules visible`;
  };
  return (
    <div data-testid="settings-roles-permissions">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[["roles", "Roles", UserCog], ["matrix", "Matrix View", Grid3X3]].map(([k, l, Icon]) => (
          <button key={k} data-testid={`roles-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
            <Icon size={14} /> {l}
          </button>
        ))}
        {tab === "roles" && <Btn className="ml-auto" onClick={() => setWizard("new")} data-testid="create-role-btn"><Plus size={14} /> Create Role</Btn>}
      </div>

      {tab === "matrix" && MatrixConsole}

      {tab === "roles" && (
        <div className="grid gap-4 md:grid-cols-2">
          {BUILTIN.map((b) => (
            <Card key={b.key} className="p-5" data-testid={`builtin-role-${b.key}`}>
              <div className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><ShieldCheck size={15} className="text-[#7B6157]" /> {b.name} <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-semibold text-[#84675B]">Built-in</span></div>
              <p className="mt-1.5 text-xs text-[#6D4C41]">{b.desc}</p>
              <p className="mt-2 text-[11px] text-[#84675B]">{builtinAccess(b.key)} · fine-tune in Matrix View</p>
            </Card>
          ))}
          {roles.map((r) => (
            <Card key={r.id} className="p-5 border-[#C8E6C9]" data-testid={`custom-role-card-${r.key}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><UserCog size={15} className="text-[#2E7D32]" /> {r.name} <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D32]">Custom</span></div>
                <div className="flex gap-1">
                  <button data-testid={`edit-role-${r.key}`} onClick={() => setWizard(r)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Pencil size={13} /></button>
                  <button data-testid={`delete-role-${r.key}`} onClick={() => setDelRole(r)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                </div>
              </div>
              {(r.summary || r.description) && <p className="mt-1.5 text-xs text-[#6D4C41]">{r.summary || r.description}</p>}
              <p className="mt-2 text-[11px] text-[#84675B]">
                {r.permissions && Object.keys(r.permissions).length
                  ? `${Object.values(r.permissions).flat().length} permissions across ${Object.keys(r.permissions).length} modules`
                  : (r.domains || []).length ? `Legacy domains: ${r.domains.join(", ")}` : "No access configured"}
              </p>
            </Card>
          ))}
          {roles.length === 0 && (
            <Card className="p-5 border-dashed flex items-center justify-center text-center md:col-span-2">
              <div>
                <Sparkles size={22} className="mx-auto text-[#5E35B1]" />
                <p className="mt-2 text-sm font-semibold text-[#3E2723]">Create your first custom role</p>
                <p className="text-xs text-[#7B6157]">Describe the duties and let AI draft a least-privilege profile you can approve.</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {wizard && meta && (
        <RoleWizard modules={meta.modules} actions={meta.actions} editRole={wizard === "new" ? null : wizard}
          onClose={() => setWizard(null)} onSaved={load} />
      )}
      <ConfirmDialog open={!!delRole} onClose={() => setDelRole(null)} onConfirm={remove}
        description={`Delete role "${delRole?.name}"? Users assigned to it will lose its permissions.`} />
    </div>
  );
}
